
'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, ArrowRight, ArrowLeft, Calendar, Clock, ChevronRight, ChevronLeft, Star, Loader2, Rocket } from 'lucide-react';
import { api } from '../services/api';
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Badge, BadgeWithIcon } from "@/components/base/badges/badges";
import { Avatar } from "@/components/base/avatar/avatar";
import { BlurImage } from "@/components/base/images/BlurImage";
import PreloadResources from '../components/PreloadResources';
import { Select } from '@/components/ui/Select';
import FeaturedPost from '@/components/blog/FeaturedPost';

import prof1 from '../assets/reviews/prof_1.webp';
import prof2 from '../assets/reviews/prof_2.webp';
import prof3 from '../assets/reviews/prof_3.webp';
import review1 from '../assets/reviews/review_1.webp';
import review2 from '../assets/reviews/review_2.webp';
import review3 from '../assets/reviews/review_3.webp';

// Dynamic import for Waitlist to match homepage behavior
const Waitlist = dynamic(() => import('@/components/Waitlist'), {
    loading: () => <div className="h-[400px] flex items-center justify-center"><div className="w-8 h-8 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" /></div>,
});

// --- Mock Data ---

// Career Outcomes (Static)
const careerOutcomes = [
    {
        id: 'out-1',
        name: "Sarah Jenkins",
        role: "Product Designer at Stripe",
        image: review1.src || review1,
        quote: "Exibell helped me restructure my portfolio and land my dream role in just 3 weeks.",
        salary: "+45% Salary Hike"
    },
    {
        id: 'out-2',
        name: "David Chen",
        role: "Senior Dev at Vercel",
        image: review2.src || review2,
        quote: "The AI interview prep was a game changer. I felt completely prepared for the technical rounds.",
        salary: "Multiple Offers"
    },
    {
        id: 'out-3',
        name: "Emily Ross",
        role: "Marketing Lead at Notion",
        image: review3.src || review3,
        quote: "I didn't know how to position my diverse experience until I used the career path tools.",
        salary: "Senior Role Secured"
    },
    {
        id: 'out-4',
        name: "Michael Chang",
        role: "Data Scientist at Netflix",
        image: prof2.src || prof2,
        quote: "The resume builder caught nuances in my experience that I had completely overlooked.",
        salary: "Relocation Package"
    },
    {
        id: 'out-5',
        name: "Sarah Lee",
        role: "UX Researcher at Google",
        image: review1.src || review1,
        quote: "The mentorship program connected me with industry veterans who guided my transition from academia.",
        salary: "Senior Level Entry"
    },
    {
        id: 'out-6',
        name: "James Wilson",
        role: "Frontend Dev at Amazon",
        image: prof3.src || prof3,
        quote: "I learned more practical skills in 2 months here than I did in my entire bootcamp.",
        salary: "+50% Salary Hike"
    }
];

const InlineNewsletter = () => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setStatus('loading');
        try {
            await api.subscribeNewsletter(email);
            setStatus('success');
            setEmail('');
        } catch (error) {
            setStatus('error');
        }
    };

    return (
        <div className="h-full min-h-[400px] rounded-2xl p-8 flex flex-col justify-center relative overflow-hidden bg-brand-50 border border-brand-100">
            {/* Abstract Shapes - Subtle */}
            <div className="absolute top-0 right-0 w-48 h-48 bg-brand-200/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-100/50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

            <div className="relative z-10 text-center md:text-left">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm border border-brand-100 mx-auto md:mx-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-600"><path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h8" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /><path d="M19 16v6" /><path d="M16 19h6" /></svg>
                </div>

                <h3 className="text-2xl font-bold mb-3 text-gray-900">Weekly newsletter</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                    No spam. Just the latest releases and tips, interesting articles, and exclusive interviews in your inbox every week.
                </p>

                {status === 'success' ? (
                    <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                        <span className="font-bold text-green-700">Subscribed! 🎉</span>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-3">
                        <Input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={setEmail}
                            className="bg-white border-brand-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:ring-brand-200"
                        />
                        <Button
                            type="submit"
                            className="w-full bg-brand-600 text-white hover:bg-brand-700 shadow-md"
                            isLoading={status === 'loading'}
                        >
                            Subscribe
                        </Button>
                        <p className="text-xs text-gray-500 mt-3">
                            Read about our <Link href="/privacy" className="underline hover:text-gray-900">privacy policy</Link>.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};


const Blog = ({ articles = [], featuredPost = null, categories = [], groups = [], currentCategory = null }) => {
    const scrollRef = React.useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    // State
    const [activeGroup, setActiveGroup] = useState("All");
    const [sortBy, setSortBy] = useState("recent"); // recent, popular, viewed
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;
    const tabsRef = React.useRef(null);

    const handleGroupChange = (group) => {
        setActiveGroup(group);
        setCurrentPage(1);
    };

    // Promo Form State
    const [promoEmail, setPromoEmail] = useState('');
    const [promoStatus, setPromoStatus] = useState('idle');

    const handlePromoSubscribe = async (e) => {
        e.preventDefault();
        if (!promoEmail) return;
        setPromoStatus('loading');
        try {
            await api.joinWaitlist({ email: promoEmail, type: 'early_access', pageSource: 'blog_promo' });
            setPromoStatus('success');
            setPromoEmail('');
        } catch (error) {
            console.error(error);
            setPromoStatus('error');
        }
    };

    // Auto-scroll effect (same as before)
    React.useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;
        let animationFrameId;
        let scrollAccumulator = 0;
        const autoScroll = () => {
            if (!isDragging && !isPaused) {
                scrollAccumulator += 0.5;
                if (scrollAccumulator >= 1) {
                    const pixelsToScroll = Math.floor(scrollAccumulator);
                    scrollAccumulator -= pixelsToScroll;
                    if (scrollContainer.scrollLeft + scrollContainer.clientWidth >= scrollContainer.scrollWidth) {
                        scrollContainer.scrollLeft = 0;
                    } else {
                        scrollContainer.scrollLeft += pixelsToScroll;
                    }
                }
            }
            animationFrameId = requestAnimationFrame(autoScroll);
        };
        animationFrameId = requestAnimationFrame(autoScroll);
        return () => cancelAnimationFrame(animationFrameId);
    }, [isDragging, isPaused]);

    // Drag handlers (same as before)
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    };
    const handleMouseLeave = () => { setIsDragging(false); setIsPaused(false); };
    const handleMouseUp = () => { setIsDragging(false); };
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        scrollRef.current.scrollLeft = scrollLeft - walk;
    };


    // Filter & Sort Logic
    const filteredArticles = React.useMemo(() => {
        let filtered = [...articles]; // Create copy to sort safely

        // Filter
        if (activeGroup !== "All") {
            filtered = filtered.filter(a => a.group === activeGroup);
        }

        // Sort
        filtered.sort((a, b) => {
            if (sortBy === 'recent') {
                return new Date(b.date) - new Date(a.date);
            } else if (sortBy === 'popular') {
                // Mock logic: assuming longer read time might correlate or random if no views data
                // In real app use: b.likes - a.likes OR b.views - a.views
                return (b.views || 0) - (a.views || 0);
            } else if (sortBy === 'viewed') {
                return (b.views || 0) - (a.views || 0);
            }
            return 0;
        });

        return filtered;
    }, [articles, activeGroup, sortBy]);

    // Pagination Logic
    const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);

    // Paginated Articles with Newsletter Insertion
    const paginatedArticles = React.useMemo(() => {
        const withNewsletter = [...filteredArticles];
        if (withNewsletter.length > 0 && activeGroup === "All") {
            withNewsletter.splice(2, 0, { type: 'newsletter', id: 'newsletter-card' });
        }

        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;

        return withNewsletter.slice(start, end);
    }, [filteredArticles, activeGroup, currentPage]);


    const showFeatured = featuredPost && !currentCategory && currentPage === 1;

    // Select Options
    const sortOptions = [
        { value: 'recent', label: 'Most recent' },
        { value: 'popular', label: 'Most popular' },
        { value: 'viewed', label: 'Most viewed' },
    ];

    return (
        <div className="min-h-screen bg-white pt-24">
            {showFeatured && <PreloadResources resources={[{ href: featuredPost.image, as: "image", options: { fetchPriority: "high" } }]} />}

            <div className="container container-wide pb-12">
                {/* Header */}
                <div className="text-center mb-16">
                    <span className="text-brand-600 font-semibold mb-2 block uppercase tracking-wider text-sm">Our Blog</span>
                    <h1 className="text-display-md md:text-display-lg font-semibold mb-6 text-gray-900">
                        Resources and <span className="text-brand-600">insights</span>
                    </h1>
                    <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
                        The latest industry news, interviews, technologies, and resources to help you build your career.
                    </p>
                </div>

                {/* Featured Post (Before Tabs) */}
                {showFeatured && (
                    <div className="mb-24">
                        <FeaturedPost
                            post={featuredPost}
                            recentPosts={filteredArticles.filter(a => a.id !== featuredPost?.id).slice(0, 3)}
                        />
                    </div>
                )}

                {/* Controls: Tabs & Sort Dropdown */}
                <div ref={tabsRef} className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 border-b border-gray-100 pb-4">
                    {/* Group Tabs - Scrollable on mobile */}
                    <div className="flex overflow-x-auto gap-1 pb-2 md:pb-0 scrollbar-none flex-1">
                        <button
                            onClick={() => handleGroupChange("All")}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 whitespace-nowrap ${activeGroup === "All"
                                ? 'bg-brand-50 text-brand-700'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            View all
                        </button>
                        {groups.map((group) => (
                            <button
                                key={group}
                                onClick={() => handleGroupChange(group)}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 whitespace-nowrap ${activeGroup === group
                                    ? 'bg-brand-50 text-brand-700'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                {group}
                            </button>
                        ))}
                    </div>

                    {/* Sort Dropdown */}
                    <div className="flex-shrink-0 w-full md:w-48">
                        <Select
                            options={sortOptions}
                            value={sortBy}
                            onChange={setSortBy}
                            placeholder="Sort by"
                        />
                    </div>
                </div>

                {/* Article Grid / Carousel */}
                <div className="mb-16">
                    {/* Mobile: Horizontal Scroll (Carousel behaviour), Desktop: Grid */}
                    <motion.div
                        className="flex overflow-x-auto snap-x snap-mandatory gap-6 md:grid md:grid-cols-2 lg:grid-cols-3 md:overflow-visible pb-8 md:pb-0 scrollbar-none"
                    >
                        <AnimatePresence mode="wait">
                            {paginatedArticles.map((item) => {
                                if (item.type === 'newsletter') {
                                    return (
                                        <motion.div
                                            key="newsletter"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.2 }}
                                            className="min-w-[85vw] md:min-w-0 snap-center md:col-span-1"
                                        >
                                            <InlineNewsletter />
                                        </motion.div>
                                    );
                                }

                                return (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        key={item.id}
                                        className="min-w-[85vw] md:min-w-0 snap-center group flex flex-col h-full bg-transparent"
                                    >
                                        {/* Image */}
                                        <div className="aspect-[16/10] w-full rounded-2xl overflow-hidden mb-6 bg-gray-100 border border-gray-100">
                                            <Link href={`/blog/${item.id}`} className="block w-full h-full">
                                                <BlurImage
                                                    src={item.image}
                                                    alt={item.title}
                                                    fill
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                                />
                                            </Link>
                                        </div>

                                        {/* Content */}
                                        <div className="flex flex-col flex-1">
                                            {/* Category & Read Time */}
                                            <div className="flex items-center gap-3 mb-3">
                                                {item.categorySlug && (
                                                    <Link href={`/blog/${item.categorySlug}`} className="z-10">
                                                        <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700 border border-brand-100 hover:bg-brand-100 transition-colors">
                                                            {item.category}
                                                        </span>
                                                    </Link>
                                                )}
                                                <span className="text-xs font-medium text-gray-500">8 min read</span>
                                            </div>

                                            {/* Title w/ Arrow */}
                                            <Link href={`/blog/${item.id}`} className="group/title block mb-2">
                                                <h3 className="text-xl font-bold text-gray-900 leading-tight flex items-start justify-between gap-2 group-hover/title:text-brand-600 transition-colors">
                                                    <span className="line-clamp-2">{item.title}</span>
                                                    <ArrowUpRight className="w-5 h-5 flex-shrink-0 text-gray-400 group-hover/title:text-brand-600 transition-colors transform group-hover/title:translate-x-0.5 group-hover/title:-translate-y-0.5" />
                                                </h3>
                                            </Link>

                                            <p className="text-gray-600 text-sm line-clamp-2 mb-6 flex-1">
                                                {item.excerpt}
                                            </p>

                                            {/* Author Footer */}
                                            <div className="flex items-center gap-3 mt-auto">
                                                <Avatar
                                                    src={item.author?.image}
                                                    name={item.author?.name}
                                                    size="sm"
                                                    className="h-8 w-8"
                                                />
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-semibold text-gray-900 leading-none mb-0.5">{item.author?.name}</span>
                                                    <span className="text-xs text-gray-500">{item.date}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </motion.div>
                </div>

                {/* Pagination */}
                <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-gray-100 mb-24">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft size={20} /> Previous
                    </button>

                    <div className="flex items-center gap-2 my-4 md:my-0">
                        {Array.from({ length: Math.max(totalPages || 1, 1) }, (_, i) => i + 1).map((page) => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${currentPage === page
                                    ? 'bg-brand-50 text-brand-700'
                                    : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                            >
                                {page}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages || 1))}
                        disabled={currentPage === (totalPages || 1)}
                        className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Next <ChevronRight size={20} />
                    </button>
                </div>

                {/* Career Outcomes Carousel */}
                <div className="mb-24">
                    <div className="flex items-center justify-between mb-8">
                        <h2 className="text-2xl font-bold text-gray-900">Latest Career Outcomes</h2>
                        <div className="flex gap-2">
                            <button
                                onClick={() => scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' })}
                                className="p-2 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <button
                                onClick={() => scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' })}
                                className="p-2 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Draggable & Auto-scrolling Container */}
                    <div
                        ref={scrollRef}
                        className="flex overflow-x-auto gap-6 pb-8 cursor-grab active:cursor-grabbing [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']"
                        onMouseDown={handleMouseDown}
                        onMouseLeave={handleMouseLeave}
                        onMouseUp={handleMouseUp}
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseMove={handleMouseMove}
                    >
                        {careerOutcomes.map((outcome) => (
                            <div
                                key={outcome.id}
                                className="min-w-[320px] md:min-w-[400px] bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col select-none group/card hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-center gap-4 mb-6">
                                    <Avatar
                                        src={outcome.image}
                                        alt={outcome.name}
                                        size="lg"
                                        contrastBorder={true}
                                        className="pointer-events-none"
                                    />
                                    <div>
                                        <h4 className="font-bold text-gray-900 group-hover/card:text-brand-700 transition-colors uppercase tracking-tight">{outcome.name}</h4>
                                        <p className="text-sm text-gray-500">{outcome.role}</p>
                                    </div>
                                </div>
                                <div className="flex gap-1 mb-4">
                                    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />)}
                                </div>
                                <p className="text-gray-700 text-md mb-6 leading-relaxed flex-1">"{outcome.quote}"</p>
                                <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between">
                                    <Badge
                                        color="brand"
                                        type="pill-color"
                                        size="sm"
                                        className="font-bold border-brand-100 uppercase tracking-widest"
                                    >
                                        {outcome.salary}
                                    </Badge>
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Verified Outcome</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Promo Banner (Split Layout) */}
                <div className="mb-24 bg-gray-50 rounded-[2.5rem] p-8 md:p-12 overflow-hidden border border-gray-100 relative">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                        style={{
                            backgroundImage: 'radial-gradient(#000 1px, transparent 1px)',
                            backgroundSize: '24px 24px'
                        }}
                    />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center relative z-10">
                        <div className="text-left">
                            <BadgeWithIcon
                                type="pill-color"
                                color="brand"
                                size="md"
                                iconLeading={Rocket}
                                className="mb-6 gap-2 px-3 py-1 bg-white border-brand-100 text-brand-700 shadow-sm w-fit"
                            >
                                Get 50% Off Early Access
                            </BadgeWithIcon>

                            <h2 className="text-display-sm font-bold text-gray-900 mb-4 leading-tight">
                                Unlock early access
                            </h2>
                            <p className="text-xl text-gray-600 mb-8 max-w-md">
                                Limited seats on the waitlist. Join now and get notified as soon as we launch to secure your discount.
                            </p>

                            {/* Form */}
                            {promoStatus === 'success' ? (
                                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 text-green-700 font-medium">
                                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">🎉</div>
                                    You've been added to the list!
                                </div>
                            ) : (
                                <form onSubmit={handlePromoSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-lg">
                                    <div className="flex-1">
                                        <Input
                                            type="email"
                                            placeholder="Enter your email"
                                            value={promoEmail}
                                            onChange={setPromoEmail}
                                            className="bg-white border-gray-200 h-12"
                                            required
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        size="lg"
                                        className="bg-brand-600 hover:bg-brand-700 text-white whitespace-nowrap shadow-lg shadow-brand-600/20 h-12 px-8"
                                        isLoading={promoStatus === 'loading'}
                                    >
                                        Get Early Access
                                    </Button>
                                </form>
                            )}
                            {promoStatus === 'error' && <p className="text-red-500 text-sm mt-3">Something went wrong. Please try again.</p>}
                        </div>

                        {/* Image Grid Collage (5 Images) - Masonry Style */}
                        <div className="relative h-[450px] w-full hidden lg:block">
                            {/* 1. Tall Right */}
                            <div className="absolute right-0 top-8 w-48 h-80 rounded-2xl overflow-hidden shadow-xl transform rotate-1 border-4 border-white z-20 hover:scale-105 transition-transform duration-500">
                                <img src={review1.src || review1} alt="Community member" className="w-full h-full object-cover" />
                            </div>

                            {/* 2. Middle Top */}
                            <div className="absolute right-[40%] top-0 w-40 h-52 rounded-2xl overflow-hidden shadow-lg transform -rotate-2 z-10 border-4 border-white hover:scale-105 transition-transform duration-500">
                                <img src={prof2.src || prof2} alt="Community member" className="w-full h-full object-cover" />
                            </div>

                            {/* 3. Middle Bottom */}
                            <div className="absolute right-[45%] bottom-16 w-36 h-48 rounded-2xl overflow-hidden shadow-lg transform rotate-3 z-30 border-4 border-white hover:scale-105 transition-transform duration-500">
                                <img src={prof1.src || prof1} alt="Community member" className="w-full h-full object-cover" />
                            </div>

                            {/* 4. Left Side */}
                            <div className="absolute left-4 bottom-28 w-40 h-40 rounded-2xl overflow-hidden shadow-md transform -rotate-6 z-10 border-4 border-white hover:scale-105 transition-transform duration-500">
                                <img src={prof3.src || prof3} alt="Community member" className="w-full h-full object-cover" />
                            </div>

                            {/* 5. Top Left Small (Decoration) */}
                            <div className="absolute left-16 top-12 w-20 h-20 rounded-2xl overflow-hidden shadow-sm transform rotate-12 z-0 opacity-80 border-2 border-white/50">
                                <img src={review2.src || review2} alt="Community member" className="w-full h-full object-cover grayscale" />
                            </div>
                        </div>
                    </div>
                </div>

            </div>

        </div>
    );
};

export default Blog;
